import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import axios from "axios";
import ProjectCard from "../components/ProjectCard";
import { PageSEO, BreadcrumbSchema, ProjectsCollectionSchema } from "../components/SEO";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GENRES = ["All", "Drama", "Horror", "Thriller", "Comedy"];
const STATUSES = ["All", "Released", "In Production", "In Development"];

export default function ProjectsPage() {
  const [projects, setProjects] = useState([]);
  const [activeGenre, setActiveGenre] = useState("All");
  const [activeStatus, setActiveStatus] = useState("All");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const res = await axios.get(`${API}/projects`);
        setProjects(res.data);
      } catch (e) {
        console.error("Failed to fetch projects", e);
      } finally {
        setLoaded(true);
      }
    };
    fetchProjects();
  }, []);

  const filtered = projects.filter((p) => {
    const genreMatch = activeGenre === "All" || p.genre.toLowerCase().includes(activeGenre.toLowerCase());
    const statusMatch = activeStatus === "All" || p.status === activeStatus;
    return genreMatch && statusMatch;
  });

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Projects", path: "/projects" },
  ];

  return (
    <div data-testid="projects-page" className="min-h-screen pt-36 pb-28 px-6 md:px-12 lg:px-24">
      <PageSEO
        title="Projects"
        description="Explore the complete slate of Mist Media productions spanning drama, horror, thriller, and comedy across film, television, and digital platforms."
        path="/projects"
        schema={loaded ? ProjectsCollectionSchema(projects) : BreadcrumbSchema(breadcrumbs)}
      />

      <div className="max-w-[1400px] mx-auto">
        {/* Breadcrumb */}
        <nav aria-label="Breadcrumb" className="mb-8">
          <ol className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold">
            <li><Link to="/" className="text-[#3f3f46] hover:text-white transition-colors">Home</Link></li>
            <li><ChevronRight size={10} className="text-[#27272A]" /></li>
            <li className="text-[#71717A]">Projects</li>
          </ol>
        </nav>

        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <div className="flex items-center gap-4 mb-4">
            <div className="w-8 h-px bg-[#38BDF8]" aria-hidden="true" />
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#38BDF8]">Our Portfolio</p>
          </div>
          <h1 className="font-['Syne'] text-5xl sm:text-6xl md:text-7xl font-extrabold tracking-[-0.04em] text-white">
            Projects
          </h1>
          <p className="mt-4 text-sm text-[#52525B] max-w-lg">
            Explore our slate of projects spanning drama, horror, thriller, and comedy across film, television, and digital.
          </p>
        </motion.div>

        {/* Filters */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mb-14"
        >
          <div className="flex flex-col sm:flex-row gap-6">
            <div>
              <p className="text-[9px] uppercase tracking-[0.3em] font-bold text-[#3f3f46] mb-3">Genre</p>
              <div className="flex flex-wrap gap-1.5" data-testid="genre-filter" role="group" aria-label="Filter by genre">
                {GENRES.map((genre) => (
                  <button
                    key={genre}
                    onClick={() => setActiveGenre(genre)}
                    data-testid={`genre-filter-${genre.toLowerCase()}`}
                    aria-pressed={activeGenre === genre}
                    className={`btn-press text-[10px] uppercase tracking-[0.15em] font-bold px-4 py-2 rounded-none border transition-all duration-300 ${
                      activeGenre === genre
                        ? "bg-white text-[#050505] border-white"
                        : "bg-transparent text-[#52525B] border-[#1A1A1A] hover:border-[#3f3f46] hover:text-white"
                    }`}
                  >
                    {genre}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-[0.3em] font-bold text-[#3f3f46] mb-3">Status</p>
              <div className="flex flex-wrap gap-1.5" data-testid="status-filter" role="group" aria-label="Filter by status">
                {STATUSES.map((status) => (
                  <button
                    key={status}
                    onClick={() => setActiveStatus(status)}
                    data-testid={`status-filter-${status.toLowerCase().replace(/\s+/g, '-')}`}
                    aria-pressed={activeStatus === status}
                    className={`btn-press text-[10px] uppercase tracking-[0.15em] font-bold px-4 py-2 rounded-none border transition-all duration-300 ${
                      activeStatus === status
                        ? "bg-white text-[#050505] border-white"
                        : "bg-transparent text-[#52525B] border-[#1A1A1A] hover:border-[#3f3f46] hover:text-white"
                    }`}
                  >
                    {status}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </motion.div>

        {loaded && (
          <div className="mb-8">
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-[#3f3f46]">
              {filtered.length} Project{filtered.length !== 1 ? "s" : ""}
            </p>
          </div>
        )}

        {!loaded && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="skeleton-card aspect-[3/4] skeleton rounded-none" />
            ))}
          </div>
        )}

        {loaded && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {filtered.map((project, i) => (
              <ProjectCard key={project.id} project={project} index={i} />
            ))}
          </div>
        )}

        {loaded && filtered.length === 0 && (
          <div className="text-center py-24">
            <p className="text-[#52525B] text-sm">No projects match the selected filters.</p>
          </div>
        )}
      </div>
    </div>
  );
}
